import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { FaPlus, FaSearch, FaEye, FaEdit, FaTrash, FaRedo, FaPrint, FaFileExcel } from 'react-icons/fa';
import {
  getEquipmentExpenses,
  deleteEquipmentExpense,
} from '../../../../redux/Slices/EquipmentExpensesSlice';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const EquipmentsExpenses = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRTL = i18n.language === 'ar';
  const [expenses, setExpenses] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [equipmentId, setEquipmentId] = useState(searchParams.get('equipment_id') || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [projectId, setProjectId] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [expenseForPrint, setExpenseForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [previewExpense, setPreviewExpense] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState(null); // 'single' or 'all'
  const [pdfRowsPerPage, setPdfRowsPerPage] = useState(0);
  const [selectedEquipmentLabel, setSelectedEquipmentLabel] = useState('');
  const [selectedProjectLabel, setSelectedProjectLabel] = useState('');
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  // Equipment dropdown
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentPagination, setEquipmentPagination] = useState(null);
  const equipmentSearchInputRef = useRef(null);

  // Project dropdown
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const projectSearchInputRef = useRef(null);

  const paginationInfo = useMemo(() => {
    if (!pagination) return { current_page: currentPage, last_page: 1 };
    return {
      current_page: pagination.current_page || pagination.page || currentPage,
      last_page: pagination.last_page || pagination.total_pages || 1,
    };
  }, [pagination, currentPage]);

  const pdfChunks = useMemo(() => {
    const rpp = pdfRowsPerPage || 18;
    if (!expenses.length) return [];
    const chunks = [];
    for (let i = 0; i < expenses.length; i += rpp) {
      chunks.push({ items: expenses.slice(i, i + rpp), startIndex: i });
    }
    return chunks;
  }, [expenses, pdfRowsPerPage]);

  const activeFiltersForPdf = useMemo(() => {
    const parts = [];
    if (searchTerm) parts.push(`${isRTL ? 'بحث' : 'Search'}: ${searchTerm}`);
    if (equipmentId) parts.push(`${isRTL ? 'المعدة' : 'Equipment'}: ${selectedEquipmentLabel || equipmentId}`);
    if (projectId) parts.push(`${isRTL ? 'المشروع' : 'Project'}: ${selectedProjectLabel || projectId}`);
    if (startDate) parts.push(`${isRTL ? 'من' : 'From'}: ${startDate}`);
    if (endDate) parts.push(`${isRTL ? 'إلى' : 'To'}: ${endDate}`);
    return parts.join(' | ');
  }, [searchTerm, equipmentId, projectId, startDate, endDate, selectedEquipmentLabel, selectedProjectLabel, isRTL]);

  const fetchExpenses = useCallback(
    async (
      page = currentPage,
      search = searchTerm,
      equipment = equipmentId,
      start = startDate,
      end = endDate,
      project = projectId,
      perPageValue = perPage
    ) => {
      try {
        setIsLoading(true);
        const payload = await dispatch(
          getEquipmentExpenses({
            page,
            per_page: perPageValue,
            search: search || undefined,
            equipment_id: equipment || undefined,
            start_date: start || undefined,
            end_date: end || undefined,
            project_id: project || undefined,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setExpenses(items);
        setPagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('equipmentExpenses.messages.fetchError'), { rtl: isRTL });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, currentPage, searchTerm, equipmentId, startDate, endDate, projectId, perPage]
  );

  const fetchEquipments = useCallback(
    async (page = equipmentPage, search = equipmentSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('equipmentExpenses.messages.noAuthToken'));
        const response = await axios.get(`${BASE_URL}/equipment`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setEquipmentOptions(items);
        setEquipmentPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('equipmentExpenses.messages.fetchEquipmentsError'), { rtl: isRTL });
      }
    },
    [equipmentPage, equipmentSearchTerm, t, isRTL]
  );

  const fetchProjects = useCallback(
    async (page = projectPage, search = projectSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('equipmentExpenses.messages.noAuthToken'));
        const response = await axios.get(`${BASE_URL}/projects`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setProjectOptions(items);
        setProjectPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('equipmentExpenses.messages.fetchProjectsError'), { rtl: isRTL });
      }
    },
    [projectPage, projectSearchTerm, t, isRTL]
  );

  useEffect(() => {
    setTitle('sidenav.equipment_expenses');
  }, [setTitle]);

  useEffect(() => {
    const measure = () => {
      const topH = document.getElementById('eq-expenses-pdf-measure-top-header')?.offsetHeight ?? 150;
      const theadH = document.getElementById('eq-expenses-pdf-measure-thead')?.offsetHeight ?? 28;
      const footerH = document.getElementById('eq-expenses-pdf-measure-footer')?.offsetHeight ?? 80;
      const rowH = document.getElementById('eq-expenses-pdf-measure-row')?.offsetHeight ?? 26;
      const availH = 1033 - topH - theadH - footerH - 20;
      setPdfRowsPerPage(Math.max(1, Math.floor(availH / rowH)));
    };
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Close dropdowns when clicking outside or when opening another dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
      const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setEquipmentDropdownOpen(false);
        setProjectDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close other dropdowns when opening a new one
  useEffect(() => {
    if (equipmentDropdownOpen) {
      setProjectDropdownOpen(false);
    }
  }, [equipmentDropdownOpen]);

  useEffect(() => {
    if (projectDropdownOpen) {
      setEquipmentDropdownOpen(false);
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    const equipmentIdFromQuery = searchParams.get('equipment_id');
    if (equipmentIdFromQuery) {
      setEquipmentId(equipmentIdFromQuery);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchExpenses(currentPage, searchTerm, equipmentId, startDate, endDate, projectId, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, equipmentId, startDate, endDate, projectId, perPage]);

  useEffect(() => {
    if (equipmentDropdownOpen) {
      fetchEquipments(equipmentPage, equipmentSearchTerm);
    }
  }, [equipmentDropdownOpen, equipmentPage, fetchEquipments]);

  useEffect(() => {
    if (projectDropdownOpen) {
      fetchProjects(projectPage, projectSearchTerm);
    }
  }, [projectDropdownOpen, projectPage, fetchProjects]);

  useEffect(() => {
    if (equipmentSearchTerm !== undefined && equipmentDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEquipmentPage(1);
        fetchEquipments(1, equipmentSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [equipmentSearchTerm, equipmentDropdownOpen, fetchEquipments]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setProjectPage(1);
        fetchProjects(1, projectSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [projectSearchTerm, projectDropdownOpen, fetchProjects]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    setSearchTerm(value);
    setCurrentPage(1);
    fetchExpenses(1, value, equipmentId, startDate, endDate, projectId, perPage);
  };

  const handleApplyPerPage = () => {
    const total = pagination?.total || 0;
    const maxAllowed = total > 0 ? total : 1000;
    const next = Math.min(Math.max(1, parseInt(perPageInput) || 1), maxAllowed);
    setPerPage(next);
    setPerPageInput(next);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setEquipmentId('');
    setStartDate('');
    setEndDate('');
    setProjectId('');
    setCurrentPage(1);
    setEquipmentSearchTerm('');
    setEquipmentPage(1);
    setProjectSearchTerm('');
    setProjectPage(1);
    setSelectedEquipmentLabel('');
    setSelectedProjectLabel('');
    setSearchParams({});
    fetchExpenses(1, '', '', '', '', '', perPage);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || amount === '') return '—';
    const num = Number(amount);
    if (isNaN(num)) return amount;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle print single equipment expense
  const handlePrintEquipmentExpense = async (expense) => {
    try {
      setExpenseForPrint(expense);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`equipment-expense-pdf-${expense.id}`);
      if (!element) {
        toast.error(t('equipmentExpenses.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: true,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(`equipment-expense-pdf-${expense.id}`);
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            clonedElement.style.textRendering = 'optimizeLegibility';

            const allElements = clonedElement.querySelectorAll('*');
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = 'bold';
                el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
                el.style.textRendering = 'optimizeLegibility';
              }
            });
          }
        }
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/png', 1.0);
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      if (imgHeight <= pdfHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
      } else {
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          const remainingHeight = imgHeight - position;
          const pageHeight = Math.min(remainingHeight, pdfHeight);
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pageHeight, undefined, 'FAST');
          position += pdfHeight;
        }
      }

      const identifier = expense.id;
      pdf.save(`ACE_EquipmentExpense_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Equipment Expense PDF error:', err);
      toast.error(t('equipmentExpenses.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setExpenseForPrint(null);
      setPdfType(null);
    }
  };

  // Handle print all equipment expenses — uses dynamic per-page chunks
  const handlePrintAll = async () => {
    if (!expenses || expenses.length === 0) {
      toast.error(t('equipmentExpenses.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      await new Promise((r) => requestAnimationFrame(r));
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      await new Promise((r) => setTimeout(r, 300));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const scale = Math.max(3, (window.devicePixelRatio || 1) * 2);

      for (let i = 0; i < pdfChunks.length; i++) {
        let element = document.getElementById(`eq-expenses-pdf-all-${i}`);
        let retries = 0;
        while (!element && retries < 10) {
          await new Promise((r) => requestAnimationFrame(r));
          element = document.getElementById(`eq-expenses-pdf-all-${i}`);
          if (!element) await new Promise((r) => setTimeout(r, 100));
          retries++;
        }
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: true,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc) => {
            const cloned = clonedDoc.getElementById(`eq-expenses-pdf-all-${i}`);
            if (cloned) {
              cloned.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              cloned.style.fontWeight = 'bold';
              cloned.style.direction = isRTL ? 'rtl' : 'ltr';
              cloned.style.setProperty('-webkit-font-smoothing', 'antialiased');
              cloned.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
              cloned.style.textRendering = 'optimizeLegibility';
              Array.from(cloned.querySelectorAll('*')).forEach((el) => {
                if (el.style) {
                  el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                  el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
                  el.style.textRendering = 'optimizeLegibility';
                }
              });
            }
          },
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgH = (canvas.height * pdfWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, Math.min(imgH, pdfHeight), undefined, 'FAST');
      }

      pdf.save(`ACE_EquipmentExpenses_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Equipment Expenses PDF error:', err);
      toast.error(t('equipmentExpenses.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف مصروفات المعدات' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle export single equipment expense to Excel
  const handleExportEquipmentExpenseToExcel = (expense) => {
    const data = [{
      [t('equipmentExpenses.tableHeaders.date', { defaultValue: 'التاريخ' })]: formatDate(expense.date) || '—',
      [t('equipmentExpenses.tableHeaders.equipment', { defaultValue: 'المعدة' })]: expense.equipment?.name_ar || expense.equipment?.name_en || '—',
      [t('equipmentExpenses.tableHeaders.project', { defaultValue: 'المشروع' })]: expense.project?.name_ar || expense.project?.name_en || '—',
      [t('equipmentExpenses.tableHeaders.description', { defaultValue: 'الوصف' })]: expense.description || '—',
      [t('equipmentExpenses.tableHeaders.amount', { defaultValue: 'المبلغ' })]: formatCurrency(expense.amount) || '—'
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'مصروف معدة' : 'Equipment Expense');
    const identifier = expense.id;
    const fileName = `${isRTL ? 'مصروف_معدة' : 'EquipmentExpense'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('equipmentExpenses.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // Handle export all equipment expenses to Excel
  const handleExportAllToExcel = () => {
    if (!expenses || expenses.length === 0) {
      toast.error(t('equipmentExpenses.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    const data = expenses.map((expense, index) => ({
      'م': (paginationInfo.current_page - 1) * perPage + (index + 1),
      [t('equipmentExpenses.tableHeaders.date', { defaultValue: 'التاريخ' })]: formatDate(expense.date) || '—',
      [t('equipmentExpenses.tableHeaders.equipment', { defaultValue: 'المعدة' })]: expense.equipment?.name_ar || expense.equipment?.name_en || '—',
      [t('equipmentExpenses.tableHeaders.project', { defaultValue: 'المشروع' })]: expense.project?.name_ar || expense.project?.name_en || '—',
      [t('equipmentExpenses.tableHeaders.description', { defaultValue: 'الوصف' })]: expense.description || '—',
      [t('equipmentExpenses.tableHeaders.amount', { defaultValue: 'المبلغ' })]: formatCurrency(expense.amount) || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'مصروفات المعدات' : 'Equipment Expenses');
    const fileName = `${isRTL ? 'مصروفات_المعدات' : 'EquipmentExpenses'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('equipmentExpenses.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  const handleDelete = async () => {
    if (!expenseToDelete) return;
    const idToDelete = expenseToDelete.id;
    const backup = expenseToDelete;
    // Optimistic remove
    setExpenses(prev => prev.filter(e => e.id !== idToDelete));
    setDeleteModalOpen(false);
    setExpenseToDelete(null);
    try {
      await dispatch(
        deleteEquipmentExpense({
          equipmentId: backup.equipment_id,
          expenseId: backup.id,
        })
      ).unwrap();
      toast.success(t('equipmentExpenses.messages.deleteSuccess'), { rtl: isRTL });
    } catch (error) {
      // Rollback
      setExpenses(prev => {
        // Insert back in original order
        const idx = prev.findIndex(e => e.id > idToDelete);
        if (idx === -1) return [...prev, backup];
        return [...prev.slice(0, idx), backup, ...prev.slice(idx)];
      });
      let errorMsg = '';
      if (error && typeof error === 'string') errorMsg = error;
      else if (error && error.message) errorMsg = error.message;
      else errorMsg = t('equipmentExpenses.messages.deleteError', { defaultValue: 'فشل الحذف، تحقق من الاتصال أو الصلاحيات' });
      toast.error(errorMsg, { rtl: isRTL });
    }
  };

  return (
    <div style={{ padding: 30, backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
        <div style={{ position: 'relative', flex: '1 1 320px' }}>
          <FaSearch style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('equipmentExpenses.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '12px 16px 12px 42px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: 8,
              color: 'white',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left',
            }}
          />
        </div>
        <div style={{ position: 'relative', flex: '1 1 320px' }} data-dropdown-container>
          <div data-dropdown-toggle onClick={() => setEquipmentDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
            <span>
              {equipmentId
                ? (isRTL
                  ? equipmentOptions.find((e) => String(e.id) === String(equipmentId))?.name_ar ||
                  equipmentOptions.find((e) => String(e.id) === String(equipmentId))?.name_en ||
                  `${t('equipmentExpenses.tableHeaders.equipment')} #${equipmentId}`
                  : equipmentOptions.find((e) => String(e.id) === String(equipmentId))?.name_en ||
                  equipmentOptions.find((e) => String(e.id) === String(equipmentId))?.name_ar ||
                  `${t('equipmentExpenses.tableHeaders.equipment')} #${equipmentId}`)
                : t('equipmentExpenses.selectEquipment')}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={equipmentDropdownOpen}
            searchTerm={equipmentSearchTerm}
            onSearchChange={setEquipmentSearchTerm}
            searchInputRef={equipmentSearchInputRef}
            items={equipmentOptions}
            pagination={equipmentPagination}
            onPageChange={setEquipmentPage}
            onClose={() => setEquipmentDropdownOpen(false)}
            onSelect={(item) => {
              setEquipmentId(String(item.id));
              setSelectedEquipmentLabel(isRTL ? (item.name_ar || item.name_en || '') : (item.name_en || item.name_ar || ''));
              setEquipmentDropdownOpen(false);
              setEquipmentSearchTerm('');
              setEquipmentPage(1);
              setCurrentPage(1);
            }}
            label={t('equipmentExpenses.tableHeaders.equipment')}
          />
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 320px' }} data-dropdown-container>
          <div data-dropdown-toggle onClick={() => setProjectDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
            <span>
              {projectId
                ? (isRTL
                  ? projectOptions.find((p) => String(p.id) === String(projectId))?.name_ar ||
                  projectOptions.find((p) => String(p.id) === String(projectId))?.name_en ||
                  `${t('equipmentExpenses.tableHeaders.project')} #${projectId}`
                  : projectOptions.find((p) => String(p.id) === String(projectId))?.name_en ||
                  projectOptions.find((p) => String(p.id) === String(projectId))?.name_ar ||
                  `${t('equipmentExpenses.tableHeaders.project')} #${projectId}`)
                : t('equipmentExpenses.selectProject')}
            </span>
            <span style={{ fontSize: 12 }}>▼</span>
          </div>
          <DropdownWithSearch
            isOpen={projectDropdownOpen}
            searchTerm={projectSearchTerm}
            onSearchChange={setProjectSearchTerm}
            searchInputRef={projectSearchInputRef}
            items={projectOptions}
            pagination={projectPagination}
            onPageChange={setProjectPage}
            onClose={() => setProjectDropdownOpen(false)}
            onSelect={(item) => {
              setProjectId(String(item.id));
              setSelectedProjectLabel(isRTL ? (item.name_ar || item.name_en || '') : (item.name_en || item.name_ar || ''));
              setProjectDropdownOpen(false);
              setProjectSearchTerm('');
              setProjectPage(1);
              setCurrentPage(1);
            }}
            label={t('equipmentExpenses.tableHeaders.project')}
          />
        </div>
        <div style={{ flex: '1 1 320px' }}>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
            placeholder={t('equipmentExpenses.startDate')}
          />
        </div>
        <div style={{ flex: '1 1 320px' }}>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...filterInputStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
            placeholder={t('equipmentExpenses.endDate')}
          />
        </div>
      </div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
            {t('common.perPage', { defaultValue: 'Per Page' })}:
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => {
                const newValue = Math.max(1, perPageInput - 1);
                setPerPageInput(newValue);
              }}
              style={{
                backgroundColor: '#1b3350',
                color: 'white',
                border: '1px solid #333',
                padding: '6px 10px',
                borderRadius: '6px 0 0 6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px'
              }}
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max={pagination?.total || 1000}
              value={perPageInput}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10) || 1;
                const maxValue = pagination?.total || 1000;
                setPerPageInput(Math.max(1, Math.min(maxValue, value)));
              }}
              style={{
                width: '70px',
                padding: '6px 10px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderLeft: 'none',
                borderRight: 'none',
                borderRadius: 0,
                color: 'white',
                fontSize: '14px',
                textAlign: 'center'
              }}
            />
            <button
              onClick={() => {
                const maxValue = pagination?.total || 1000;
                const newValue = Math.min(maxValue, perPageInput + 1);
                setPerPageInput(newValue);
              }}
              style={{
                backgroundColor: '#1b3350',
                color: 'white',
                border: '1px solid #333',
                padding: '6px 10px',
                borderRadius: '0 6px 6px 0',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '32px'
              }}
            >
              +
            </button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            {t('common.apply', { defaultValue: 'تطبيق' })}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleReset}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              width: 'auto',
            }}
          >
            {t('equipmentExpenses.reset')}
          </button>
          <button
            onClick={() => navigate('/equipment-expenses/add')}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center',
              width: 'auto',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <FaPlus /> {t('equipmentExpenses.addNew')}
          </button>
          <button
            onClick={() => {
              if (!expenses || expenses.length === 0) {
                toast.error(t('equipmentExpenses.print.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
                return;
              }
              setPreviewType('all');
              setPreviewExpense(null);
              setIsPreviewOpen(true);
            }}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaEye /> {t('equipmentExpenses.print.previewAll', { defaultValue: 'معاينة PDF للكل' })}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: isGeneratingPdf ? 0.6 : 1
            }}
          >
            <FaPrint /> {isGeneratingPdf ? t('equipmentExpenses.print.generating', { defaultValue: 'جاري الطباعة...' }) : t('equipmentExpenses.print.all', { defaultValue: 'طباعة الكل' })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaFileExcel /> {t('equipmentExpenses.export.all', { defaultValue: 'تصدير Excel الكل' })}
          </button>
        </div>
      </div>

      <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                {[
                  t('equipmentExpenses.tableHeaders.number'),
                  t('equipmentExpenses.tableHeaders.equipment'),
                  t('equipmentExpenses.tableHeaders.date'),
                  t('equipmentExpenses.tableHeaders.amount'),
                  isRTL ? t('equipmentExpenses.tableHeaders.descriptionAr') : t('equipmentExpenses.tableHeaders.descriptionEn'),
                  t('equipmentExpenses.tableHeaders.project'),
                  t('equipmentExpenses.tableHeaders.actions')
                ].map(
                  (header) => (
                    <th
                      key={header}
                      style={{
                        padding: 14,
                        textAlign: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 14,
                        borderBottom: '1px solid #333',
                      }}
                    >
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#ccc' }}>
                    {t('equipmentExpenses.loading')}
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#ccc' }}>
                    {t('equipmentExpenses.noData')}
                  </td>
                </tr>
              ) : (
                expenses.map((expense, index) => (
                  <tr key={expense.id || index} style={{ borderBottom: '1px solid #333' }}>
                    <td style={cellStyle}>{(paginationInfo.current_page - 1) * perPage + (index + 1)}</td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL
                        ? expense.equipment?.name_ar || expense.equipment?.name_en || '—'
                        : expense.equipment?.name_en || expense.equipment?.name_ar || '—'}
                    </td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{formatDate(expense.expense_date)}</td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{expense.amount || '—'}</td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL ? (expense.description_ar || '—') : (expense.description_en || '—')}
                    </td>
                    <td style={{ ...cellStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {isRTL
                        ? expense.project?.name_ar || expense.project?.name_en || '—'
                        : expense.project?.name_en || expense.project?.name_ar || '—'}
                    </td>
                    <td style={{ ...cellStyle, minWidth: 150 }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'nowrap' }}>
                        <ActionButton
                          title={t('equipmentExpenses.print.previewSingle', { defaultValue: 'معاينة PDF' })}
                          onClick={() => {
                            setPreviewExpense(expense);
                            setPreviewType('single');
                            setIsPreviewOpen(true);
                          }}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t('equipmentExpenses.print.single', { defaultValue: 'طباعة' })}
                          onClick={() => handlePrintEquipmentExpense(expense)}
                          disabled={isGeneratingPdf}
                          style={{ opacity: isGeneratingPdf ? 0.6 : 1 }}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t('equipmentExpenses.export.single', { defaultValue: 'تصدير Excel' })}
                          onClick={() => handleExportEquipmentExpenseToExcel(expense)}
                          bg="#28a745"
                        >
                          <FaFileExcel />
                        </ActionButton>
                        <ActionButton
                          title={t('equipmentExpenses.actions.edit')}
                          onClick={() =>
                            navigate(`/equipment-expenses/edit/${expense.equipment_id}/${expense.id}`)
                          }
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t('equipmentExpenses.actions.delete')}
                          bg="#c0392b"
                          onClick={() => {
                            setExpenseToDelete(expense);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16, borderTop: '1px solid #333' }}>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={paginationInfo.current_page === 1}
            style={paginationButton(paginationInfo.current_page === 1)}
          >
            {t('equipmentExpenses.previous')}
          </button>
          <span style={{ padding: 8 }}>
            {paginationInfo.current_page} / {paginationInfo.last_page}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => (prev >= paginationInfo.last_page ? paginationInfo.last_page : prev + 1))
            }
            disabled={paginationInfo.current_page >= paginationInfo.last_page}
            style={paginationButton(paginationInfo.current_page >= paginationInfo.last_page)}
          >
            {t('equipmentExpenses.next')}
          </button>
        </div>
      </div>
      {deleteModalOpen && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalCardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>{t('equipmentExpenses.deleteModal.title')}</h3>
            <p style={{ marginBottom: 24 }}>
              {t('equipmentExpenses.deleteModal.message')}
            </p>
            <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'flex-start', gap: 12, flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  setDeleteModalOpen(false);
                  setExpenseToDelete(null);
                }}
              >
                {t('equipmentExpenses.deleteModal.cancel')}
              </button>
              <button style={modalPrimaryButtonStyle} onClick={handleDelete}>
                {t('equipmentExpenses.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden measure elements for dynamic PDF pagination — always in DOM */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', top: 0, width: '714px', backgroundColor: '#ffffff', fontFamily: '"Times New Roman", serif', fontSize: '11px', boxSizing: 'border-box' }}
      >
        <div id="eq-expenses-pdf-measure-top-header" style={{ paddingBottom: '8px', marginBottom: '8px', borderBottom: '4px double #000' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} crossOrigin="anonymous" />
          <h2 style={{ textAlign: 'center', fontSize: '16px', fontWeight: '700', margin: '8px 0 4px' }}>{isRTL ? 'تقرير مصروفات المعدات' : 'Equipment Expenses Report'}</h2>
          <p style={{ textAlign: 'center', fontSize: '11px', margin: '0 0 4px' }}>Date</p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000' }}>
          <thead id="eq-expenses-pdf-measure-thead">
            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
              <th style={{ border: '1px solid #000', padding: '6px', width: '36px', textAlign: 'center' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Date</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Equipment</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Project</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Description</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr id="eq-expenses-pdf-measure-row">
              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>1</td>
              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>—</td>
              <td style={{ border: '1px solid #000', padding: '6px', verticalAlign: 'middle' }}>—</td>
              <td style={{ border: '1px solid #000', padding: '6px', verticalAlign: 'middle' }}>—</td>
              <td style={{ border: '1px solid #000', padding: '6px', verticalAlign: 'middle' }}>—</td>
              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>—</td>
            </tr>
          </tbody>
        </table>
        <div id="eq-expenses-pdf-measure-footer">
          <img src={`${publicUrl}/FooterAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000' }} crossOrigin="anonymous" />
        </div>
      </div>

      {/* Hidden PDF Template — single expense */}
      {expenseForPrint && pdfType === 'single' && (
        <div
          id={`equipment-expense-pdf-${expenseForPrint.id}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
          }}
        >
          <SingleEquipmentExpensePrintTemplate
            expense={expenseForPrint}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
          />
        </div>
      )}

      {/* Hidden print pages for all expenses — one page per chunk */}
      {pdfChunks.length > 0 && (
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px' }}>
          {pdfChunks.map((chunk, i) => (
            <EquipmentExpensesPdfPage
              key={i}
              id={`eq-expenses-pdf-all-${i}`}
              chunk={chunk}
              chunkIndex={i}
              totalChunks={pdfChunks.length}
              isPreview={false}
              isRTL={isRTL}
              t={t}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              activeFilters={activeFiltersForPdf}
            />
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen}
        title={previewType === 'all' ? t('equipmentExpenses.preview.titleAll') : t('equipmentExpenses.preview.title')}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewExpense(null);
          setPreviewType(null);
        }}
        onPrint={previewType === 'all' ? handlePrintAll : previewExpense ? () => handlePrintEquipmentExpense(previewExpense) : undefined}
        isGeneratingPdf={isGeneratingPdf}
      >
        {previewType === 'single' && previewExpense && (
          <div
            id={`equipment-expense-pdf-preview-${previewExpense.id}`}
            style={{
              width: '794px',
              minHeight: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
            }}
          >
            <SingleEquipmentExpensePrintTemplate
              expense={previewExpense}
              isRTL={isRTL}
              t={t}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
            />
          </div>
        )}
        {previewType === 'all' && pdfChunks.map((chunk, i) => (
          <EquipmentExpensesPdfPage
            key={i}
            chunk={chunk}
            chunkIndex={i}
            totalChunks={pdfChunks.length}
            isPreview={true}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            activeFilters={activeFiltersForPdf}
          />
        ))}
      </PdfPreviewModal>
    </div>
  );
};

// Print Template for Single Equipment Expense
const SingleEquipmentExpensePrintTemplate = ({ expense, isRTL, t, formatDate, formatCurrency, publicUrl, headerLogo }) => {
  return (
    <div style={{
      border: '5px solid #000',
      padding: '3px',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box',
      width: '100%',
      minHeight: '1043px',
    }}>
      <div style={{
        border: '2px solid #000',
        minHeight: '1033px',
        height: '1033px',
        padding: '18px 18px 10px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        direction: isRTL ? 'rtl' : 'ltr',
        backgroundColor: '#ffffff',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${headerLogo})`,
          backgroundSize: '95%',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt="Header"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              marginBottom: '8px',
              borderBottom: '4px double #000',
              paddingBottom: '8px',
              flexShrink: 0
            }}
            crossOrigin="anonymous"
          />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
            <div style={{ marginTop: '8px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
              <h2 style={{ textAlign: 'center', fontSize: '16px', fontWeight: '700', color: '#000080', margin: '0 0 4px', fontFamily: '"Times New Roman", serif' }}>
                {isRTL ? 'تقرير مصروف المعدة' : 'Equipment Expense Report'}
              </h2>
              <p style={{ textAlign: 'center', fontSize: '11px', margin: '0 0 8px', fontFamily: '"Times New Roman", serif', color: '#333' }}>
                {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('equipmentExpenses.printTemplate.date')}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('equipmentExpenses.printTemplate.equipment')}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('equipmentExpenses.printTemplate.project')}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('equipmentExpenses.printTemplate.description')}</th>
                    <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('equipmentExpenses.printTemplate.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(expense.expense_date || expense.date) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{isRTL ? (expense.equipment?.name_ar || expense.equipment?.name_en) : (expense.equipment?.name_en || expense.equipment?.name_ar) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{isRTL ? (expense.project?.name_ar || expense.project?.name_en) : (expense.project?.name_en || expense.project?.name_ar) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{isRTL ? (expense.description_ar || expense.description_en) : (expense.description_en || expense.description_ar) || '—'}</td>
                    <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(expense.amount) || '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
            <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000' }} crossOrigin="anonymous" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Per-page PDF template for "All Equipment Expenses" report
const EquipmentExpensesPdfPage = ({ id, chunk, chunkIndex, totalChunks, isPreview = false, isRTL, t, formatDate, formatCurrency, publicUrl, headerLogo, activeFilters }) => {
  if (!chunk?.items?.length) return null;
  const thStyle = { border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.9)', verticalAlign: 'middle' };
  const tdBase = { border: '1px solid #000', padding: '5px 6px', verticalAlign: 'middle', fontSize: '10.5px' };

  return (
    <div
      id={id}
      style={{
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman", "Times", serif',
        boxSizing: 'border-box',
        overflow: 'hidden',
        border: '5px solid #000',
        padding: '3px',
        ...(isPreview ? { marginBottom: '20px', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' } : {}),
      }}
    >
      <div style={{
        border: '2px solid #000',
        height: '1033px',
        padding: '14px 18px 10px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        direction: isRTL ? 'rtl' : 'ltr',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}>
        {/* Watermark */}
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          backgroundImage: `url(${headerLogo})`, backgroundSize: '95%',
          backgroundPosition: 'center center', backgroundRepeat: 'no-repeat',
          opacity: 0.08, zIndex: 0, pointerEvents: 'none',
        }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <div style={{ flexShrink: 0, paddingBottom: '8px', borderBottom: '4px double #000', marginBottom: '8px' }}>
            <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} crossOrigin="anonymous" />
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflow: 'hidden', paddingBottom: '110px' }}>
            {/* Title */}
            <h2 style={{ textAlign: 'center', fontSize: '15px', fontWeight: '700', color: '#000080', margin: '0 0 3px', fontFamily: '"Times New Roman", serif' }}>
              {isRTL ? 'تقرير مصاريف المعدات' : 'Equipment Expenses Report'}
            </h2>
            {/* Date */}
            <p style={{ textAlign: 'center', fontSize: '10.5px', margin: '0 0 4px', color: '#333', fontFamily: '"Times New Roman", serif' }}>
              {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            {/* Filters */}
            {activeFilters && (
              <div style={{ textAlign: 'center', fontSize: '10px', color: '#555', margin: '0 0 4px', fontStyle: 'italic' }}>
                {activeFilters}
              </div>
            )}
            {/* Page counter */}
            <div style={{ textAlign: isRTL ? 'left' : 'right', fontSize: '10px', color: '#555', margin: '0 0 4px' }}>
              {isRTL ? `صفحة ${chunkIndex + 1} من ${totalChunks}` : `Page ${chunkIndex + 1} of ${totalChunks}`}
            </div>
            {/* Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.9)' }}>
                  <th style={{ ...thStyle, width: '36px' }}>#</th>
                  <th style={thStyle}>{t('equipmentExpenses.printTemplate.date')}</th>
                  <th style={thStyle}>{t('equipmentExpenses.printTemplate.equipment')}</th>
                  <th style={thStyle}>{t('equipmentExpenses.printTemplate.project')}</th>
                  <th style={{ ...thStyle, textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.printTemplate.description')}</th>
                  <th style={thStyle}>{t('equipmentExpenses.printTemplate.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {chunk.items.map((expense, idx) => {
                  const bg = idx % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(248,248,248,0.7)';
                  return (
                    <tr key={expense.id ?? idx} style={{ backgroundColor: bg }}>
                      <td style={{ ...tdBase, textAlign: 'center', backgroundColor: bg }}>{chunk.startIndex + idx + 1}</td>
                      <td style={{ ...tdBase, textAlign: 'center', backgroundColor: bg }}>{formatDate(expense.expense_date || expense.date) || '—'}</td>
                      <td style={{ ...tdBase, textAlign: isRTL ? 'right' : 'left', backgroundColor: bg }}>{isRTL ? (expense.equipment?.name_ar || expense.equipment?.name_en) : (expense.equipment?.name_en || expense.equipment?.name_ar) || '—'}</td>
                      <td style={{ ...tdBase, textAlign: isRTL ? 'right' : 'left', backgroundColor: bg }}>{isRTL ? (expense.project?.name_ar || expense.project?.name_en) : (expense.project?.name_en || expense.project?.name_ar) || '—'}</td>
                      <td style={{ ...tdBase, textAlign: isRTL ? 'right' : 'left', backgroundColor: bg }}>{isRTL ? (expense.description_ar || expense.description_en) : (expense.description_en || expense.description_ar) || '—'}</td>
                      <td style={{ ...tdBase, textAlign: 'center', backgroundColor: bg }}>{formatCurrency(expense.amount) || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px' }}>
            <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000' }} crossOrigin="anonymous" />
          </div>
        </div>
      </div>
    </div>
  );
};

const cellStyle = {
  padding: 14,
  textAlign: 'center',
  color: 'white',
  fontSize: 14,
};

const paginationButton = (disabled) => ({
  padding: '8px 16px',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const ActionButton = ({ children, onClick, title, bg = '#2f3644' }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: 6,
      padding: '8px 10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 38,
    }}
  >
    {children}
  </button>
);

const filterInputStyle = {
  width: '100%',
  padding: 12,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const dropdownPaginationButtonStyle = (disabled) => ({
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={dropdownMenuStyle} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={label === t('equipmentExpenses.tableHeaders.equipment')
            ? t('equipmentExpenses.dropdown.searchEquipment')
            : t('equipmentExpenses.dropdown.searchProject')}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
              {t('equipmentExpenses.dropdown.page')} {pagination.current_page} {t('equipmentExpenses.dropdown.of')} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
            {t('equipmentExpenses.dropdown.noResults')}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{ padding: '10px 12px', borderTop: '1px solid #333', cursor: 'pointer', direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatEntity = (entity) => {
  if (!entity) return '—';
  const code = entity.code ? `${entity.code} - ` : '';
  const isRTL = i18n.language === 'ar';
  const name = isRTL
    ? (entity.name_ar || entity.name_en || '')
    : (entity.name_en || entity.name_ar || '');
  return `${code}${name}` || `#${entity.id}`;
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: 16,
};

const modalCardStyle = {
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  width: '100%',
  maxWidth: 420,
};

const modalPrimaryButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

const modalSecondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

export default EquipmentsExpenses;
